import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Clock, CheckCircle, XCircle, ChevronDown, Info, MessageSquare, Lock } from 'lucide-react';
import ApprovalDetailsView, {
  TYPE_ICONS, TYPE_LABELS, getApprovalDisplayName
} from '../components/ApprovalDetailsView';

const STATUS_COLORS = {
  PENDING:  { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock },
  APPROVED: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
  REJECTED: { bg: 'bg-red-100',   text: 'text-red-800',   icon: XCircle },
  CANCELLED:{ bg: 'bg-gray-100',  text: 'text-gray-700',  icon: XCircle },
};

export default function MyApprovalsPage() {
  const [approvals, setApprovals]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter]         = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    loadApprovals();
    const interval = setInterval(loadApprovals, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadApprovals = async () => {
    try {
      const response = await api.get('/approvals/user-pending');
      setApprovals(response.data || []);
    } catch (err) {
      console.error('Failed to load approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseData = (raw) => {
    if (!raw) return {};
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
    catch { return {}; }
  };

  const filtered = approvals.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter.toUpperCase();
  });

  const counts = {
    all:      approvals.length,
    pending:  approvals.filter(a => a.status === 'PENDING').length,
    approved: approvals.filter(a => a.status === 'APPROVED').length,
    rejected: approvals.filter(a => a.status === 'REJECTED').length,
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
        <p className="text-gray-500">Loading approvals...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
          <Clock className="w-8 h-8 text-blue-500" />
          My Approval Requests
        </h1>
        <p className="text-gray-600">Track the status of your pending requests</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { key: 'all',      label: 'Total',    color: 'bg-gray-100 text-gray-700' },
          { key: 'pending',  label: 'Pending',  color: 'bg-amber-100 text-amber-700' },
          { key: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700' },
          { key: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
        ].map(card => (
          <button
            key={card.key}
            onClick={() => setFilter(card.key)}
            className={`p-3 rounded-lg transition-all ${
              filter === card.key
                ? `${card.color} ring-2 ring-offset-2`
                : `${card.color} opacity-70 hover:opacity-100`
            }`}
          >
            <p className="text-xs font-medium mb-1">{card.label}</p>
            <p className="text-2xl font-bold">{counts[card.key]}</p>
          </button>
        ))}
      </div>

      {/* Approvals List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              {filter === 'pending' ? 'No pending requests' : 'No requests'}
            </p>
            <p className="text-sm text-gray-500">
              {filter === 'approved' ? 'Your approved requests appear here' : 'Nothing to show'}
            </p>
          </div>
        ) : (
          filtered.map(approval => {
            const data       = parseData(approval.requested_data);
            const StatusIcon = STATUS_COLORS[approval.status]?.icon || Clock;
            const colors     = STATUS_COLORS[approval.status] || STATUS_COLORS.PENDING;
            const isExpanded = expandedId === approval.id;
            const headerName = getApprovalDisplayName(data);

            return (
              <div key={approval.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : approval.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <StatusIcon className={`w-5 h-5 ${colors.text}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-lg">{TYPE_ICONS[approval.type]}</span>
                        <h3 className="font-semibold text-gray-900">
                          {TYPE_LABELS[approval.type] || approval.type}
                        </h3>
                        {headerName && (
                          <span className="text-sm text-gray-500">— {headerName}</span>
                        )}
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">
                        Requested: {new Date(approval.created_at).toLocaleDateString()}
                        {' at '}
                        {new Date(approval.created_at).toLocaleTimeString()}
                      </p>
                    </div>

                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${colors.bg} ${colors.text}`}>
                        {approval.status}
                      </span>
                      {approval.status !== 'PENDING' && (
                        <p className="text-xs text-gray-500 mt-1">
                          {approval.status === 'APPROVED' && '✓ Approved'}
                          {approval.status === 'REJECTED' && '✗ Rejected'}
                        </p>
                      )}
                    </div>

                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`} />
                  </div>
                </div>

                {/* Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                    {/* What you submitted */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-400" />
                        Submitted Details
                      </h4>
                      <ApprovalDetailsView data={data} type={approval.type} showMeta={false} />
                    </div>

                    {/* Timeline */}
                    <div className="bg-white p-3 rounded border border-gray-200">
                      <h4 className="font-semibold text-sm mb-2">Timeline</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex gap-3">
                          <span className="text-amber-600 font-semibold min-w-20">Requested:</span>
                          <span className="text-gray-700">
                            {new Date(approval.created_at).toLocaleString()}
                          </span>
                        </div>
                        {approval.status === 'APPROVED' && approval.approved_at && (
                          <div className="flex gap-3">
                            <span className="text-green-600 font-semibold min-w-20">Approved:</span>
                            <span className="text-gray-700">
                              {new Date(approval.approved_at).toLocaleString()}
                            </span>
                          </div>
                        )}
                        {approval.status === 'REJECTED' && approval.rejected_at && (
                          <div className="flex gap-3">
                            <span className="text-red-600 font-semibold min-w-20">Rejected:</span>
                            <span className="text-gray-700">
                              {new Date(approval.rejected_at).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Rejection Reason */}
                    {approval.status === 'REJECTED' && approval.rejected_reason && (
                      <div className="bg-red-50 p-3 rounded border border-red-200">
                        <h4 className="font-semibold text-sm text-red-900 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3.5 h-3.5" /> Rejection Reason
                        </h4>
                        <p className="text-sm text-red-800">{approval.rejected_reason}</p>
                      </div>
                    )}

                    {/* Admin Notes */}
                    {approval.admin_notes && (
                      <div className="bg-blue-50 p-3 rounded border border-blue-200">
                        <h4 className="font-semibold text-sm text-blue-900 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3.5 h-3.5" /> Admin Notes
                        </h4>
                        <p className="text-sm text-blue-800">{approval.admin_notes}</p>
                      </div>
                    )}

                    {/* Status banner */}
                    {approval.status === 'PENDING' && (
                      <div className="bg-amber-50 p-3 rounded border border-amber-200 flex items-start gap-2">
                        <Lock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm text-amber-800">
                            <strong>Waiting for admin approval.</strong> You'll be notified when this request is reviewed.
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            This request is locked — only an admin can approve, reject, or cancel it.
                          </p>
                        </div>
                      </div>
                    )}

                    {approval.status === 'APPROVED' && (
                      <div className="bg-green-50 p-3 rounded border border-green-200">
                        <p className="text-sm text-green-800">
                          <strong>✓ Request Approved!</strong> Your {(TYPE_LABELS[approval.type] || approval.type).toLowerCase()} has been created and is available in the system.
                        </p>
                      </div>
                    )}

                    {approval.status === 'REJECTED' && (
                      <div className="bg-red-50 p-3 rounded border border-red-200">
                        <p className="text-sm text-red-800">
                          <strong>✗ Request Rejected.</strong> Please contact your administrator for more information or try again with different details.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">How does this work?</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>✓ When you create a project, add items, sale challan or update PO status, an approval request is submitted</li>
          <li>✓ Admin will review your request and approve or reject it</li>
          <li>✓ Pending requests are locked — you cannot edit or cancel them. Only admin can.</li>
          <li>✓ Once approved, your action is completed in the system</li>
        </ul>
      </div>
    </div>
  );
}
